// src/components/cab/cabHelpers.js
// Helpers partagés du socle « Le Cabinet » — extraits de HomePage /
// DossiersPanel / DossierDetailScreen où ils vivaient en 2-3 copies
// divergentes. Toute page « Le Cabinet » doit les importer d'ici.

import { AGENTS } from '../../data/agents';
import { cabMatiere } from '../../theme';
import { supabase } from '../../lib/supabase';

export const DAY = 86400000;

// ── Décisions (skill_jobs) — LA définition partagée ─────────────────────────
// L'Accueil et Dossiers divergeaient (24 h vs 3 j glissants) : un travail livré
// mais non arbitré finissait par DISPARAÎTRE de l'écran sans que l'avocat ait
// tranché. La sémantique vit désormais ici, une seule fois :
//   • « décision en attente »  = completed SANS validated_at — sans limite de
//     temps : tant que l'avocat n'a pas validé, ça reste devant lui ;
//   • « le cabinet travaille » = pending | running ;
//   • la fenêtre temporelle ne s'applique QU'AUX jobs déjà validés (on les
//     garde VALIDATED_WINDOW_DAYS jours dans le fil comme travail récemment
//     livré, puis ils sortent des signaux).
export const VALIDATED_WINDOW_DAYS = 3;

export function isPendingDecision(job) {
  return !!job && job.status === 'completed' && !job.validated_at;
}

export function isWorkingJob(job) {
  return !!job && (job.status === 'pending' || job.status === 'running');
}

// Expression de filtre PostgREST commune aux deux clients d'accès :
// supabase-js la consomme telle quelle via .or(...), le fetch brut (sbGet)
// l'enveloppe `or=(...)`. `sinceIso` = borne basse des jobs DÉJÀ validés.
// NB : tant que la migration 060 (colonne validated_at) n'est pas appliquée,
// PostgREST renvoie une erreur sur ce filtre — les appelants doivent déjà
// retomber sur [] (sbGet et .then(undefined, …) le font), l'UI reste debout.
export function skillJobsSignalsOr(sinceIso) {
  return `status.in.(pending,running),and(status.eq.completed,validated_at.is.null),and(status.eq.completed,validated_at.gte.${sinceIso})`;
}

// ── Comptes EXACTS côté base — mêmes prédicats que isPendingDecision /
// isWorkingJob, exprimés en filtres PostgREST. Un .select() chargé puis compté
// côté client est plafonné par son .limit() (et par max-rows PostgREST) : le
// chiffre ment dès que la population dépasse le plafond. head:true +
// count:'exact' ne transfère AUCUNE ligne et compte juste. Renvoie null si le
// compte est indisponible (client absent, migration 060 pas appliquée,
// réseau) — l'appelant choisit son repli, comme pour validateSkillJob.
export async function countPendingDecisions(lawyerId) {
  if (!supabase || !lawyerId) return null;
  const { count, error } = await supabase
    .from('skill_jobs')
    .select('id', { count: 'exact', head: true })
    .eq('lawyer_id', lawyerId)
    .eq('status', 'completed')
    .is('validated_at', null);
  if (error) { console.error('[cabHelpers] countPendingDecisions:', error.message); return null; }
  return count ?? 0;
}

export async function countWorkingJobs(lawyerId) {
  if (!supabase || !lawyerId) return null;
  const { count, error } = await supabase
    .from('skill_jobs')
    .select('id', { count: 'exact', head: true })
    .eq('lawyer_id', lawyerId)
    .in('status', ['pending', 'running']);
  if (error) { console.error('[cabHelpers] countWorkingJobs:', error.message); return null; }
  return count ?? 0;
}

// Valide une décision préparée (RPC validate_skill_job, migration 060).
// Côté base, la RPC ne pose validated_at QUE si le job est un completed
// non-validé appartenant à l'avocat authentifié — et renvoie le timestamp
// posé (vide sinon). Renvoie { ok, validated_at? , error? } : jamais de throw,
// l'appelant décide quoi rafraîchir ; l'échec est loggé mais silencieux à
// l'écran (pattern maison — l'UI retombe sur son état courant).
export async function validateSkillJob(jobId) {
  if (!supabase) return { ok: false, error: 'client supabase non configuré' };
  try {
    const { data, error } = await supabase.rpc('validate_skill_job', { p_job_id: jobId });
    if (error) {
      console.error('[cabHelpers] validate_skill_job:', error.message);
      return { ok: false, error: error.message };
    }
    // Normalise le retour (scalaire timestamptz, ou ligne si SETOF/TABLE).
    const row = Array.isArray(data) ? (data[0] ?? null) : data;
    const validated_at = row && typeof row === 'object' ? (row.validated_at ?? null) : (row ?? null);
    if (!validated_at) return { ok: false, error: 'job non éligible (déjà validé, absent, ou pas à vous)' };
    return { ok: true, validated_at };
  } catch (e) {
    console.error('[cabHelpers] validate_skill_job:', e);
    return { ok: false, error: e?.message || String(e) };
  }
}

// Map skill_slug → agent (skill exact, sinon secrétariat pour secretaire-*).
export function agentForSlug(slug) {
  if (!slug) return null;
  const exact = AGENTS.find(a => a.skills?.includes(slug) || a.primarySkill === slug);
  if (exact) return exact;
  if (slug.startsWith('secretaire')) return AGENTS.find(a => a.id === 'sophie') || null;
  return null;
}

// Slug → Title Case brut (« collab-penal » → « Collab Penal »). Dépannage
// seulement : préférer skillTaskLabel pour tout libellé montré à l'avocat.
export function humanizeSlug(slug) {
  return slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'une analyse';
}

// skill_slug → libellé de tâche LISIBLE pour « {agent} travaille sur … ».
// On ne montre pas le slug technique brut (« Collab Penal »).
const COLLAB_TASK = {
  penal: 'un dossier pénal', civil: 'un dossier civil', corporel: 'un dossier de préjudice corporel',
  famille: 'un dossier familial', affaires: "un dossier d'affaires", social: 'un dossier social',
  travail: 'un dossier social', commercial: "un dossier d'affaires", societes: "un dossier d'affaires",
};
const SECRETAIRE_TASK = {
  emails: 'les emails du cabinet', agenda: "l'agenda", courriers: 'les courriers', cabinet: 'le cabinet',
};
export function skillTaskLabel(slug) {
  if (!slug) return 'une analyse';
  if (slug.startsWith('secretaire-')) return SECRETAIRE_TASK[slug.slice('secretaire-'.length)] || 'le secrétariat';
  if (slug.startsWith('collab-')) {
    const m = slug.slice('collab-'.length);
    return COLLAB_TASK[m] || `un dossier ${cabMatiere(m).label.toLowerCase()}`;
  }
  if (slug.startsWith('specialiste-')) return 'une analyse spécialisée';
  if (slug === 'general-chat') return 'une consultation';
  return slug.replace(/-/g, ' ');
}

// Délai relatif { label, urgent } (« aujourd'hui », « J-3 », « passé »).
export function relDelta(iso) {
  if (!iso) return null;
  const days = Math.round((new Date(iso) - new Date()) / DAY);
  if (days < 0) return { label: 'passé', urgent: false };
  if (days === 0) return { label: "aujourd'hui", urgent: true };
  return { label: `J-${days}`, urgent: days <= 3 };
}

// Variante courte pour les chips (« auj. », « J-3 », « passé »).
export function relLabel(iso) {
  if (!iso) return null;
  const days = Math.round((new Date(iso) - new Date()) / DAY);
  if (days < 0) return 'passé';
  if (days === 0) return 'auj.';
  return `J-${days}`;
}

export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Horodatage narratif du fil (« Aujourd'hui · 14:32 », « Hier · … », « mar. · … »).
export function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / DAY);
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diff === 0) return `Aujourd'hui · ${time}`;
  if (diff === 1) return `Hier · ${time}`;
  if (diff > 1 && diff < 7) return `${d.toLocaleDateString('fr-FR', { weekday: 'short' })} · ${time}`;
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${time}`;
}

export function fileExt(name = '') {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]{1,5})$/);
  return m ? m[1] : '';
}
